import {
  datadogRum,
  RumEvent,
  RumInitConfiguration,
} from '@datadog/browser-rum';
import { datadogLogs, LogsInitConfiguration } from '@datadog/browser-logs';

let hasDataDogInitialised = false;

export const defaultRUMConfig: RumInitConfiguration = {
  allowedTracingUrls: [{ match: /http(s?).*/, propagatorTypes: ['datadog'] }],
  applicationId: '',
  beforeSend: (event: RumEvent) => {
    // We don't want to send requestParams as it might contain sensitive data
    if (event.context && 'requestParams' in event.context) {
      delete event.context['requestParams'];
    }

    if (event.type === 'error') {
      const excludedPrefixes = ['Warning', 'webpack-dev-server'];
      // Exclude errors that start with the specified prefixes
      if (
        excludedPrefixes.some(prefix => event.error.message.includes(prefix))
      ) {
        return false;
      }
    }

    return true;
  },
  clientToken: '',
  excludedActivityUrls: [url => url.includes('google')],
  site: 'datadoghq.com',
  service: '',
  trackResources: true,
  version: '',
};

export const defaultLogsConfig: LogsInitConfiguration = {
  clientToken: '',
  env: '',
  forwardErrorsToLogs: true,
  service: '',
  site: 'datadoghq.com',
  version: '',
};

export const initDataDog = (
  dataDogLogsConfig: LogsInitConfiguration,
  dataDogRUMConfig: RumInitConfiguration,
) => {
  hasDataDogInitialised = true;

  datadogLogs.init({ ...defaultLogsConfig, ...dataDogLogsConfig });
  datadogRum.init({ ...defaultRUMConfig, ...dataDogRUMConfig });
};

export const setGlobalContext = (context: any) => {
  if (!hasDataDogInitialised) return;

  if (!context) return;

  datadogLogs.setGlobalContext(context);
  datadogRum.setGlobalContext(context);
};

export const getSessionId = (): string | undefined => {
  if (!hasDataDogInitialised) return undefined;

  const internalContext = datadogRum.getInternalContext();
  return internalContext?.session_id;
};

export class CustomError extends Error {
  constructor(
    public errors: [] = [],
    public errorMsg: string = 'Default Error message',
    public code?: number,
    public status?: number,
  ) {
    super(errorMsg); // Pass message to parent Error constructor

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, CustomError);
    } else {
      this.stack = new Error().stack;
    }
  }
  toJSON() {
    return {
      errors: this.errors,
      message: this.message,
      errorMsg: this.errorMsg,
      code: this.code,
      status: this.status,
    };
  }
}

export const addError = (error: unknown, context?: object | undefined) => {
  if (hasDataDogInitialised) {
    datadogRum.addError(error, context);
  }
};

export const logMessage = (
  message: string,
  messageContext?: object | undefined,
) => {
  if (hasDataDogInitialised) {
    datadogLogs.logger.log(message, messageContext);
  }
};

export const isDataDogInitialised = () => hasDataDogInitialised;
export type { RumInitConfiguration, LogsInitConfiguration };
